import React, {Fragment, useState} from 'react';
import PropTypes from 'prop-types';
import {snakeCase} from 'change-case';
import moment from 'moment/moment';
import {useTranslate} from '@computerrock/formation-i18n';
import {calendarIcon, Checkbox, ContentBlock, ContentItem, DateField, Form, Input, TextAreaField, ToggleSwitch, useStyles} from '@ace-de/ui-components';
import {SearchAutocomplete} from '@ace-de/eua-google-maps-client';
import {europeanCountries} from '@ace-de/eua-entity-types';
import serviceManager from '../../ServiceManager';

const RentalCarSubForm = props => {
    const {cx} = useStyles();
    const googleSearchAutocompleteService = serviceManager.loadService('googleSearchAutocompleteService');
    const {service} = props;
    const {createTranslateShorthand, translate} = useTranslate();
    const translateForm = createTranslateShorthand('rental_car_sub_form');
    const [emptyPickUpAddress, setEmptyPickUpAddress] = useState(false);
    const [emptyReturnAddress, setEmptyReturnAddress] = useState(false);
    const [emptyDateTime, setEmptyDateTime] = useState({
        startDateTime: false,
        endDateTime: false,
    });
    const [locations, setLocations] = useState({
        startingAddress: service?.startingAddress,
        destinationAddress: service?.destinationAddress,
    });

    const parseAddress = (field, value) => {
        field === 'startingAddress' ? setEmptyPickUpAddress(!value) : setEmptyReturnAddress(!value);

        googleSearchAutocompleteService.getGeolocationFromString(value).then(result => {
            let address = null;

            if (result && result.address) {
                const country = result.address.countryCode
                    ? europeanCountries[result.address.countryCode]?.name
                    : '';

                address = {
                    street: result.address.street || '',
                    postCode: result.address.postCode,
                    city: result.address.city,
                    country: country ? translate(`global.country.${snakeCase(country)}`) : '',
                    countryCode: result.address.countryCode,
                    formattedAddress: [result.address.street,
                        result.address.postCode,
                        result.address.city,
                        country ? translate(`global.country.${snakeCase(country)}`) : ''].filter(Boolean).join(', '),
                    location: {
                        longitude: result.location?.lng,
                        latitude: result.location?.lat,
                    },
                };
                setLocations(prevState => ({
                    ...prevState,
                    [field]: address,
                }));
                const addressIsEmpty = !['formattedAddress', 'city', 'street'].some(key => address[key]);

                if (field === 'startingAddress') {
                    setEmptyPickUpAddress(addressIsEmpty);
                } else {
                    setEmptyReturnAddress(addressIsEmpty);
                }
                return;
            }
            setLocations(prevState => ({
                ...prevState,
                [field]: address,
            }));
            if (field === 'startingAddress') {
                setEmptyPickUpAddress(true);
            } else {
                setEmptyReturnAddress(true);
            }
        });
    };

    return (
        <Form name="rentalCar">
            {formValues => {
                const minEndDate = formValues?.startDateTime || service?.startDateTime || '';
                const formattedMinEndDate = moment(minEndDate, ['YYYY-MM-DD', 'DD.MM.YYYY']).add(1, 'days').format('DD.MM.YYYY');
                return (
                    <Fragment>
                        <h3
                            className={cx([
                                'global!ace-u-typography--variant-h3',
                                'global!ace-u-margin--small-bottom-24',
                            ])}
                        >
                            {translateForm('title.add_details')}
                        </h3>
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-margin--small-bottom-16',
                            ])}
                        >
                            {translateForm('title.period_of_rental')}
                        </p>
                        <ContentBlock className={cx('global!ace-u-margin--small-bottom-24')}>
                            <ContentItem className={cx('ace-c-content-item--base-span-6')}>
                                <DateField
                                    name="startDateTime"
                                    label={translateForm('date_field_label.rental_starts')}
                                    icon={calendarIcon}
                                    format="DD.MM.YYYY"
                                    className={cx('global!ace-u-width--full')}
                                    value={service?.startDateTime || ''}
                                    onBlur={value => setEmptyDateTime(prevState => ({
                                        ...prevState,
                                        startDateTime: !value,
                                        ...(formValues?.endDateTime && {
                                            endDateTime: value
                                                && moment(formValues.endDateTime, ['YYYY-MM-DD', 'DD.MM.YYYY'])
                                                    .isSameOrBefore(moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY'])),
                                        }),
                                    }))}
                                    errors={emptyDateTime.startDateTime ? [translate('global.error.empty_mandatory_field')] : []}
                                />
                            </ContentItem>
                            <ContentItem className={cx('ace-c-content-item--base-span-6')}>
                                <DateField
                                    name="endDateTime"
                                    label={translateForm('date_field_label.rental_ends')}
                                    icon={calendarIcon}
                                    format="DD.MM.YYYY"
                                    className={cx('global!ace-u-width--full')}
                                    value={service?.endDateTime || ''}
                                    onBlur={value => setEmptyDateTime({
                                        endDateTime: !value
                                            || (formValues?.startDateTime
                                                && moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY'])
                                                    .isSameOrBefore(moment(formValues.startDateTime, ['YYYY-MM-DD', 'DD.MM.YYYY']))),
                                    })}
                                    errors={emptyDateTime.endDateTime ? [translate('global.error.empty_mandatory_field')] : []}
                                    minDate={formattedMinEndDate}
                                />
                            </ContentItem>
                        </ContentBlock>
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-margin--small-bottom-16',
                            ])}
                        >
                            {translateForm('text.place_of_rental')}
                        </p>
                        <SearchAutocomplete
                            placeholder={translateForm('input_placeholder.pick_up_address')}
                            name="formattedStartingAddress"
                            label={translateForm('input_label.pick_up_address')}
                            className={cx([
                                'global!ace-u-width--full',
                                'global!ace-u-margin--x-large-bottom-32',
                                'global!ace-u-margin--small-bottom-24',
                            ])}
                            onInputBlur={() => setEmptyPickUpAddress(!locations.startingAddress)}
                            onOptionSelect={value => parseAddress('startingAddress', value)}
                            value={formValues?.hasOwnProperty('startingAddress')
                                ? formValues.formattedStartingAddress
                                : service?.startingAddress
                                    ? service.startingAddress.formattedAddress
                                    || Object.keys(service?.startingAddress)
                                        .filter(key => key !== 'country' && key !== 'location' && key !== 'persistenceState')
                                        .map(key => (service?.startingAddress[key]))
                                        .filter(value => !!value)
                                        .join(' ')
                                    : ''}
                            onChange={() => setLocations(prevState => ({
                                ...prevState,
                                startingAddress: false,
                            }))}
                            errors={emptyPickUpAddress ? [translate('global.error.empty_location')] : []}
                            shouldFilterChildren={false}
                        />
                        <Input
                            name="startingAddress"
                            type="hidden"
                            value={locations?.startingAddress || ''}
                        />
                        <div
                            className={cx([
                                'global!ace-u-flex',
                                'global!ace-u-margin--small-bottom-24',
                            ])}
                        >
                            <p className={cx(['global!ace-u-flex--small-grow-1', 'global!ace-u-margin--small-right-24'])}>
                                {translateForm('toggle_switch_label.return_rental_address_different')}
                            </p>
                            <ToggleSwitch
                                name="isDestinationAddressDifferent"
                                value={true}
                                isSelected={!!service?.destinationAddress
                                    && Object.keys(service.destinationAddress).length > 0}
                            />
                        </div>
                        {!!formValues?.isDestinationAddressDifferent
                            && (
                                <Fragment>
                                    <SearchAutocomplete
                                        placeholder={translateForm('input_placeholder.return_address')}
                                        name="formattedDestinationAddress"
                                        label={translateForm('input_label.return_address')}
                                        className={cx([
                                            'global!ace-u-width--full',
                                            'global!ace-u-margin--x-large-bottom-32',
                                            'global!ace-u-margin--small-bottom-24',
                                        ])}
                                        onInputBlur={() => setEmptyReturnAddress(!locations.destinationAddress)}
                                        onOptionSelect={value => parseAddress('destinationAddress', value)}
                                        value={formValues?.hasOwnProperty('formattedDestinationAddress')
                                            ? formValues.formattedDestinationAddress
                                            : service?.destinationAddress
                                                ? service.destinationAddress.formattedAddress
                                                || Object.keys(service?.destinationAddress)
                                                    .filter(key => key !== 'country' && key !== 'location' && key !== 'persistenceState')
                                                    .map(key => (service?.destinationAddress[key]))
                                                    .filter(value => !!value)
                                                    .join(' ')
                                                : ''}
                                        onChange={() => setLocations(prevState => ({
                                            ...prevState,
                                            destinationAddress: false,
                                        }))}
                                        errors={emptyReturnAddress ? [translate('global.error.empty_location')] : []}
                                        shouldFilterChildren={false}
                                    />
                                    <Input
                                        name="destinationAddress"
                                        type="hidden"
                                        value={locations?.destinationAddress || ''}
                                    />
                                </Fragment>
                            )
                        }
                        <Checkbox
                            className={cx(['global!ace-u-margin--small-bottom-24'])}
                            name="isDelivered"
                            value={true}
                            isSelected={!!service?.isDelivered}
                        >
                            {translateForm('checkbox_label.vehicle_delivered')}
                        </Checkbox>
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-margin--small-bottom-16',
                            ])}
                        >
                            {translateForm('title.remarks')}
                        </p>
                        <TextAreaField
                            placeholder={translateForm('text_area_placeholder.rental_car_additional_comment')}
                            name="comment"
                            value={service?.comment}
                            maxLength={250}
                            isResizable={false}
                            className={cx([
                                'ace-c-text-area--small',
                                'global!ace-u-margin--small-bottom-64',
                                'global!ace-u-width--full',
                            ])}
                            label={translateForm('text_area_label.rental_car_additional_comment')}
                        />
                    </Fragment>
                );
            }}
        </Form>
    );
};

RentalCarSubForm.propTypes = {
    service: PropTypes.object.isRequired,
};

export default RentalCarSubForm;
